import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Skeleton, Stack, Button,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Add, GridView, TableRows } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import HubServiceSelectCard from '../../components/services/HubServiceSelectCard';
import HubServicesTable from '../../components/services/HubServicesTable';
import HubServiceFormDialog, {
  valuesFromService,
  type HubServiceFormValues,
} from '../../components/services/HubServiceFormDialog';
import {
  HUB_META, resolveServiceDisplayName, type ServiceHubType, type HubService,
} from '../../constants/serviceHubs';
import { resolveOnDemandChromeAccent } from '../../constants/onDemandTheme';
import {
  listHubServices, toggleHubService, createHubService,
  updateHubService, deleteHubService,
} from '../../services/serviceHubService';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectEnabledModules } from '../../store/slices/tenantSlice';

const VALID_HUBS: ServiceHubType[] = ['transport', 'delivery', 'on_demand'];
type HubViewMode = 'grid' | 'table';

type CorePreset = {
  slug: string;
  title: string;
  subtitle: string;
  fallbackColor: string;
  fallbackIcon: string;
  moduleKey: string;
};

const TRANSPORT_CORE: CorePreset[] = [
  {
    slug: 'transport',
    title: 'Taxi',
    subtitle: 'Rides, drivers, bidding, zones & earnings',
    fallbackColor: '#2563eb',
    fallbackIcon: 'LocalTaxi',
    moduleKey: 'transportation',
  },
];

const DELIVERY_CORE: CorePreset[] = [
  {
    slug: 'food-delivery',
    title: 'Food',
    subtitle: 'Restaurants, menus, live orders & partners',
    fallbackColor: '#ea580c',
    fallbackIcon: 'Restaurant',
    moduleKey: 'food',
  },
  {
    slug: 'grocery-delivery',
    title: 'Grocery',
    subtitle: 'Stores, products, slots & deliveries',
    fallbackColor: '#16a34a',
    fallbackIcon: 'ShoppingCart',
    moduleKey: 'grocery',
  },
  {
    slug: 'porter-service',
    title: 'Porter',
    subtitle: 'Parcel & goods delivery, vehicles & multi-stop',
    fallbackColor: '#0f766e',
    fallbackIcon: 'LocalShipping',
    moduleKey: 'porter',
  },
];

const ON_DEMAND_CORE: CorePreset[] = [
  {
    slug: 'home-services',
    title: 'Services',
    subtitle: 'Electrician, plumber, cleaning & more',
    fallbackColor: '#6D28D9',
    fallbackIcon: 'Build',
    moduleKey: 'on_demand',
  },
];

function serviceModuleKey(service: HubService): string | undefined {
  const settings = (service.settings || {}) as Record<string, unknown>;
  if (typeof settings.platformModule === 'string') return settings.platformModule;
  if (service.slug === 'transport') return 'transportation';
  if (service.slug === 'food-delivery') return 'food';
  if (service.slug === 'grocery-delivery') return 'grocery';
  if (service.slug === 'porter-service') return 'porter';
  if (service.slug === 'home-services') return 'on_demand';
  return service.moduleKey || service.serviceCode;
}

function loadViewMode(hub: ServiceHubType): HubViewMode {
  const saved = localStorage.getItem(`hub-view-v2-${hub}`);
  if (saved === 'grid' || saved === 'table') return saved;
  return 'table';
}

function displayMeta(service: HubService) {
  const preset = [...TRANSPORT_CORE, ...DELIVERY_CORE, ...ON_DEMAND_CORE].find((p) => p.slug === service.slug);
  const isOnDemand = service.hubType === 'on_demand' || service.slug === 'home-services';
  if (preset) {
    const raw = service.themeColor || preset.fallbackColor;
    return {
      displayTitle: preset.title,
      displaySubtitle: preset.subtitle,
      icon: service.icon || preset.fallbackIcon,
      themeColor: isOnDemand ? resolveOnDemandChromeAccent(raw) : raw,
    };
  }
  const fallback = isOnDemand ? '#6D28D9' : '#0891b2';
  return {
    displayTitle: resolveServiceDisplayName(service.hubType, service.slug, service.name) || service.name,
    displaySubtitle: service.description || 'Service',
    icon: service.icon || 'Build',
    themeColor: isOnDemand
      ? resolveOnDemandChromeAccent(service.themeColor || fallback)
      : (service.themeColor || fallback),
  };
}

type DisplayRow = HubService & { displayTitle: string; displaySubtitle: string };

export default function ServiceHubPage() {
  const { hubType: hubParam } = useParams<{ hubType: ServiceHubType }>();
  const { pathname } = useLocation();
  // Static routes `/hubs/delivery` and `/hubs/transport` have no :hubType param.
  const fromPath = pathname.split('/').filter(Boolean)[1];
  const hubCandidate = (hubParam || fromPath || 'transport') as ServiceHubType;
  const hub = VALID_HUBS.includes(hubCandidate) ? hubCandidate : 'transport';
  const meta = HUB_META[hub];
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);
  const [viewMode, setViewMode] = useState<HubViewMode>(() => loadViewMode(hub));
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<HubService | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<DisplayRow | null>(null);
  const [statusToggleId, setStatusToggleId] = useState<string | null>(null);

  useEffect(() => {
    setViewMode(loadViewMode(hub));
  }, [hub]);

  const { data: services = [], isLoading, isFetching } = useQuery({
    queryKey: ['hub-services', hub],
    queryFn: () => listHubServices(hub),
  });

  const presets = useMemo(() => {
    const all = hub === 'transport'
      ? TRANSPORT_CORE
      : hub === 'on_demand'
        ? ON_DEMAND_CORE
        : DELIVERY_CORE;
    if (isSuperAdmin) return all;
    return all.filter((preset) => enabledModules.includes(preset.moduleKey));
  }, [hub, isSuperAdmin, enabledModules]);

  const allowedCreateModules = useMemo(() => {
    if (isSuperAdmin) return ['food', 'grocery', 'porter'] as Array<'food' | 'grocery' | 'porter'>;
    return (['food', 'grocery', 'porter'] as const).filter((m) => enabledModules.includes(m));
  }, [isSuperAdmin, enabledModules]);

  const canAdd = hub === 'delivery' && allowedCreateModules.length > 0;

  const rows: DisplayRow[] = useMemo(() => {
    // Trust API filtering (tenant modules). Avoid hiding newly enabled modules
    // that are not yet present in the login-session Redux module list.
    const visibleServices = services;

    if (hub === 'transport' || hub === 'on_demand') {
      return presets
        .map((preset) => {
          const service = visibleServices.find((s) => s.slug === preset.slug);
          if (!service) return null;
          return { ...service, ...displayMeta(service), name: preset.title };
        })
        .filter(Boolean) as DisplayRow[];
    }

    // Delivery: Food / Grocery / Porter first, then custom services
    const coreSlugs = new Set(DELIVERY_CORE.map((p) => p.slug));
    const ordered: HubService[] = [];
    for (const preset of DELIVERY_CORE) {
      const found = visibleServices.find((s) => s.slug === preset.slug);
      if (found) ordered.push(found);
    }
    for (const s of visibleServices) {
      if (!coreSlugs.has(s.slug)) ordered.push(s);
    }
    return ordered.map((service) => ({ ...service, ...displayMeta(service) }));
  }, [hub, presets, services]);

  const setHubViewMode = (mode: HubViewMode) => {
    setViewMode(mode);
    localStorage.setItem(`hub-view-v2-${hub}`, mode);
  };

  const openService = (service: HubService) => {
    navigate(`/hubs/${hub}/${service.slug}/masters`);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['hub-services', hub] });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => toggleHubService(id, isActive),
    onSuccess: () => {
      invalidate();
      setDeactivateTarget(null);
    },
  });

  const handleToggleActive = (row: DisplayRow, isActive: boolean) => {
    if (toggleMut.isPending) return;
    if (isActive) {
      setStatusToggleId(row._id);
      toggleMut.mutate(
        { id: row._id, isActive: true },
        { onSettled: () => setStatusToggleId(null) },
      );
      return;
    }
    setDeactivateTarget(row);
  };

  const deactivateMessage = (row: DisplayRow) => {
    const name = row.displayTitle || row.name;
    if (hub === 'transport') {
      return `Deactivate "${name}"? Taxi will be marked inactive and hidden from customer and driver mobile apps until you turn it back on.`;
    }
    return `Deactivate "${name}"? The service will be marked inactive and hidden from mobile apps until you turn it back on.`;
  };

  const saveMut = useMutation({
    mutationFn: async (values: HubServiceFormValues) => {
      if (formMode === 'create') {
        return createHubService(hub, {
          name: values.name.trim(),
          description: values.description.trim(),
          icon: values.icon,
          themeColor: values.themeColor,
          settings: { platformModule: values.platformModule },
          moduleKey: values.platformModule,
        });
      }
      if (!editing) throw new Error('Nothing to edit');
      return updateHubService(editing._id, {
        name: values.name.trim(),
        description: values.description.trim(),
        icon: values.icon,
        themeColor: values.themeColor,
      });
    },
    onSuccess: () => {
      setFormOpen(false);
      setEditing(null);
      setFormError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg || 'Could not save service');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteHubService(id),
    onSuccess: invalidate,
  });

  const openCreate = () => {
    setFormMode('create');
    setEditing(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (service: HubService) => {
    setFormMode('edit');
    setEditing(service);
    setFormError(null);
    setFormOpen(true);
  };

  const confirmDelete = (id: string) => {
    if (window.confirm('Delete this custom sub-service? This cannot be undone.')) {
      deleteMut.mutate(id);
    }
  };

  return (
    <PageShell>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {canAdd && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={openCreate}
            sx={{ height: 36, px: 1.75 }}
          >
            Add service
          </Button>
        )}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v: HubViewMode | null) => v && setHubViewMode(v)}
          sx={{
            bgcolor: 'background.paper',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.78rem',
              px: 1.25,
              py: 0.5,
            },
          }}
        >
          <ToggleButton value="table">
            <TableRows fontSize="small" sx={{ mr: 0.5 }} /> Table
          </ToggleButton>
          <ToggleButton value="grid">
            <GridView fontSize="small" sx={{ mr: 0.5 }} /> Grid
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {isLoading || isFetching ? (
        viewMode === 'grid' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 1.75,
            }}
          >
            {(presets.length || 2) > 0 && [1, 2, 3].slice(0, Math.max(presets.length, 2)).map((n) => (
              <Skeleton key={n} variant="rounded" height={128} sx={{ borderRadius: 2.5 }} />
            ))}
          </Box>
        ) : (
          <HubServicesTable
            rows={[]}
            loading
            hub={hub}
            accentColor={meta.color}
            onOpen={openService}
            onToggleActive={() => undefined}
          />
        )
      ) : viewMode === 'table' ? (
        <HubServicesTable
          rows={rows}
          hub={hub}
          accentColor={meta.color}
          canMutate={canAdd}
          onOpen={openService}
          onToggleActive={handleToggleActive}
          togglePendingId={statusToggleId}
          onEdit={openEdit}
          onDelete={confirmDelete}
        />
      ) : rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2.5 }}>
          <Typography color="text.secondary" sx={{ mb: canAdd ? 1.5 : 0 }}>
            No sub-services in {meta.label}.
          </Typography>
          {canAdd && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add service
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.75,
          }}
        >
          {rows.map((row) => (
            <HubServiceSelectCard
              key={row._id}
              tile={{
                slug: row.slug,
                title: row.displayTitle,
                subtitle: row.displaySubtitle,
                color: row.themeColor || meta.color,
                icon: row.icon || 'Build',
                hubLabel: meta.label,
                ready: true,
                active: !!row.isActive,
                isSystem: row.isSystem,
              }}
              onOpen={() => openService(row)}
              onEdit={canAdd ? () => openEdit(row) : undefined}
              onDelete={canAdd && !row.isSystem ? () => confirmDelete(row._id) : undefined}
            />
          ))}
        </Box>
      )}

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate service?"
        message={deactivateTarget ? deactivateMessage(deactivateTarget) : ''}
        confirmLabel="Deactivate"
        confirmColor="warning"
        loading={toggleMut.isPending}
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={() => {
          if (!deactivateTarget) return;
          setStatusToggleId(deactivateTarget._id);
          toggleMut.mutate(
            { id: deactivateTarget._id, isActive: false },
            { onSettled: () => setStatusToggleId(null) },
          );
        }}
      />

      {canAdd && (
        <HubServiceFormDialog
          open={formOpen}
          mode={formMode}
          initial={editing ? valuesFromService(editing) : null}
          saving={saveMut.isPending}
          error={formError}
          allowedModules={allowedCreateModules}
          onClose={() => { if (!saveMut.isPending) setFormOpen(false); }}
          onSubmit={(values) => saveMut.mutate(values)}
        />
      )}
    </PageShell>
  );
}
